import { isSingleFieldOutcomes } from "utils";

import { CURRENCY_CODE, CurrencyCode } from "consts";

import { ACCOUNTING_METHODOLOGY, CarbonProgram, CarbonProgramOutcomesResponse } from "models";

import { allotmentCappedPayout } from "pages/app/field-timeline/utils/PayoutUtils";
import { HomeSegment, hasFieldManagementPayout } from "pages/app/home/hero/homeSegment";

/** a single figure has min === max */
export type PayoutRange = { min: number; max: number };

/**
 * why a payout can't be shown yet: no allotment for the year, no outcomes / project area (inventory), no field with complete data,
 * or a request behind the figures failed
 */
export type PayoutPendingReason = "allotment" | "outcomes" | "data" | "unavailable";

export type HomePayoutSummary = {
  /** "none": the segment has no payout source; "pending": a source exists but can't be calculated yet */
  status: "known" | "pending" | "none";
  pendingReason: PayoutPendingReason | null;
  total: PayoutRange | null;
  /** null: the part does not apply to the farm */
  fromFieldManagement: PayoutRange | "pending" | null;
  fromFootprinting: PayoutRange | "pending" | null;
  currency: CurrencyCode;
};

export type PayoutSummaryInput = {
  segment: HomeSegment;
  carbonProgram: CarbonProgram | null | undefined;
  /** the outcomes run of the farm's fields; only read for the Soil+ 3 allotment */
  programOutcomes: CarbonProgramOutcomesResponse | undefined;
  /** the reporting year's totals of the outcomes run (useCarbonProgramImpact) */
  outcomeImpact: { totalPayoutMin: number; totalPayoutAvg: number };
  /** both Soil+ 3 flags on and a reporting year with allotments: the payout is the minimum outcome capped at the allotment */
  useAllotmentPayout: boolean;
  inventoryImpact: { hideInventoryPayouts: boolean; totalPayoutMin: number; totalPayoutMax: number; totalPracticeBasedPayout: number } | null;
  footprinting: { totalPayout: number; hasPayouts: boolean; unavailable?: boolean };
  /** without fields there is nothing to calculate for field management: it is 0, not pending */
  hasFields: boolean;
  /** at least one enrolled field has complete data and so a payout for the reporting year, as counted on the fields page */
  hasPayoutPrediction: boolean;
  /** the outcomes request failed: the field management part can't be shown */
  unavailable?: boolean;
};

const single = (amount: number): PayoutRange => ({ min: amount, max: amount });

const notNegative = ({ min, max }: PayoutRange): PayoutRange => ({ min: Math.max(0, min), max: Math.max(0, max) });

// the allotment maths is shared with the old farm card's RemovalRow (allotmentCappedPayout)
const fieldManagementPayout = ({
  carbonProgram,
  programOutcomes,
  outcomeImpact,
  useAllotmentPayout,
  inventoryImpact,
  hasPayoutPrediction,
}: PayoutSummaryInput): PayoutRange | PayoutPendingReason => {
  if (carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory) {
    if (!inventoryImpact || inventoryImpact.hideInventoryPayouts) return "outcomes";
    return carbonProgram.platformFeatures?.fieldManagement?.outcomeBasedIncentives
      ? { min: inventoryImpact.totalPayoutMin, max: inventoryImpact.totalPayoutMax }
      : single(inventoryImpact.totalPracticeBasedPayout);
  }

  const { totalPayoutMin, totalPayoutAvg } = outcomeImpact;
  if (!hasPayoutPrediction) return "data";
  if (!useAllotmentPayout) return single(totalPayoutAvg);

  const soilPlus3Data = Object.values(programOutcomes ?? {})
    .filter(isSingleFieldOutcomes)
    .find((field) => field.soilPlus3Data)?.soilPlus3Data;
  const pointPrice = carbonProgram?.fieldsManagement?.payouts?.removals?.pointPrice ?? 0;
  const capped = allotmentCappedPayout(soilPlus3Data, pointPrice, totalPayoutMin);
  // an allotment of 0 is a known figure, only a missing one is still pending
  return capped === null ? "allotment" : single(capped);
};

export const summarisePayout = (input: PayoutSummaryInput): HomePayoutSummary => {
  const { segment, carbonProgram, footprinting } = input;
  const currency = carbonProgram?.currency ?? CURRENCY_CODE.EUR;
  const withFieldManagementPayout = hasFieldManagementPayout(segment);
  const hasFootprintingPayout =
    (segment === "removals-footprinting" || segment === "fields-footprinting" || segment === "footprinting-only") && footprinting.hasPayouts;

  if (!withFieldManagementPayout && !hasFootprintingPayout)
    return { status: "none", pendingReason: null, total: null, fromFieldManagement: null, fromFootprinting: null, currency };

  if ((withFieldManagementPayout && input.unavailable) || (hasFootprintingPayout && footprinting.unavailable))
    return {
      status: "pending",
      pendingReason: "unavailable",
      total: null,
      fromFieldManagement: withFieldManagementPayout ? "pending" : null,
      fromFootprinting: hasFootprintingPayout ? "pending" : null,
      currency,
    };

  const fieldManagement = withFieldManagementPayout ? (input.hasFields ? fieldManagementPayout(input) : single(0)) : null;
  if (typeof fieldManagement === "string")
    return {
      status: "pending",
      pendingReason: fieldManagement,
      total: null,
      fromFieldManagement: "pending",
      fromFootprinting: hasFootprintingPayout ? "pending" : null,
      currency,
    };

  const fromFieldManagement = fieldManagement === null ? null : notNegative(fieldManagement);
  const fromFootprinting = hasFootprintingPayout ? notNegative(single(footprinting.totalPayout)) : null;
  const total = {
    min: (fromFieldManagement?.min ?? 0) + (fromFootprinting?.min ?? 0),
    max: (fromFieldManagement?.max ?? 0) + (fromFootprinting?.max ?? 0),
  };

  return { status: "known", pendingReason: null, total, fromFieldManagement, fromFootprinting, currency };
};
